'use client';

import Link from 'next/link';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { BRAND_LOGO_HEIGHT } from '@/lib/brand/brand-assets';
import { Container, PrimaryButton } from './shared';
import { useCadrinoCopy } from '@/lib/cadrino-landing/useCadrinoCopy';
import {
  buildMarketingNavLabels,
  getMarketingNavLinks,
  isMarketingNavLinkActive,
  marketingNavLinkClass,
  marketingNavLinkClassMobile,
  resolveMarketingNavHref,
} from '@/lib/cadrino-landing/marketing-nav';
import { useMarketingNavHash } from '@/hooks/useMarketingNavHash';
import { useWebsitePagesCopy } from '@/lib/cadrino-landing/useWebsitePagesCopy';
import { handleLandingAnchorClick } from '@/lib/cadrino-landing/scroll-to-section';

const HEADER_HEIGHT = 64;

export function CadrinoMarketingHeader() {
  const pages = useWebsitePagesCopy();
  const landing = useCadrinoCopy();
  const { language, setLanguage } = useLandingLanguage();
  const pathname = usePathname();
  const hash = useMarketingNavHash();
  const [open, setOpen] = useState(false);

  const links = getMarketingNavLinks(
    buildMarketingNavLabels(pages.nav, {
      how: landing.nav.how,
      contact: landing.nav.contact,
    })
  );

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200 bg-cadrino-bg/85 supports-[backdrop-filter]:backdrop-blur-xl">
        <Container className="flex h-16 items-center justify-between">
          <BrandLogo href="/" variant="forLightBg" height={BRAND_LOGO_HEIGHT.header} priority />

          <nav className="hidden items-center gap-8 md:flex">
            {links.map((link) => {
              const active = isMarketingNavLinkActive(pathname, hash, link);
              const href = resolveMarketingNavHref(link, pathname);
              if (link.isAnchor && href.startsWith('#')) {
                return (
                  <a
                    key={link.id}
                    href={href}
                    className={marketingNavLinkClass(active)}
                    aria-current={active ? 'page' : undefined}
                    onClick={(e) => handleLandingAnchorClick(e, href)}
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.id}
                  href={href}
                  className={marketingNavLinkClass(active)}
                  aria-current={active ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden items-center gap-4 md:flex">
            <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-0.5 text-xs">
              {(['sr', 'en'] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setLanguage(lang)}
                  className={cn(
                    'min-w-[2.1rem] rounded-lg px-2.5 py-1 font-medium uppercase transition-colors',
                    language === lang
                      ? 'bg-slate-200 text-slate-900'
                      : 'text-cadrino-muted hover:text-slate-700'
                  )}
                >
                  {lang}
                </button>
              ))}
            </div>
            <Link
              href="/login"
              className="px-1.5 text-sm font-medium text-cadrino-muted transition-colors hover:text-slate-900"
            >
              {pages.nav.signIn}
            </Link>
            <PrimaryButton href="#contact" className="h-9 whitespace-nowrap px-5 text-xs shadow-sm">
              {pages.nav.bookDemo}
            </PrimaryButton>
          </div>

          <button
            type="button"
            className="text-slate-700 md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </Container>

        {open && (
          <div className="border-t border-slate-200 bg-cadrino-surface/95 md:hidden">
            <Container className="flex flex-col gap-4 py-4">
              {links.map((link) => {
                const active = isMarketingNavLinkActive(pathname, hash, link);
                const href = resolveMarketingNavHref(link, pathname);
                if (link.isAnchor && href.startsWith('#')) {
                  return (
                    <a
                      key={link.id}
                      href={href}
                      className={marketingNavLinkClassMobile(active)}
                      aria-current={active ? 'page' : undefined}
                      onClick={(e) => {
                        handleLandingAnchorClick(e, href);
                        setOpen(false);
                      }}
                    >
                      {link.label}
                    </a>
                  );
                }
                return (
                  <Link
                    key={link.id}
                    href={href}
                    className={marketingNavLinkClassMobile(active)}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="flex gap-2">
                {(['sr', 'en'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setLanguage(lang)}
                    className={cn(
                      'rounded-lg border border-slate-300 px-3 py-1.5 text-xs uppercase',
                      language === lang && 'bg-slate-200'
                    )}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              <Link
                href="/login"
                className="text-sm font-medium text-cadrino-muted transition-colors hover:text-slate-900"
                onClick={() => setOpen(false)}
              >
                {pages.nav.signIn}
              </Link>
              <PrimaryButton href="#contact" onClick={() => setOpen(false)}>
                {pages.nav.bookDemo}
              </PrimaryButton>
            </Container>
          </div>
        )}
      </header>
      <div className="shrink-0" style={{ height: HEADER_HEIGHT }} aria-hidden />
    </>
  );
}
